import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as api from "./adminApi";
import {
  AdminShell,
  Area,
  Btn,
  Card,
  Field,
  InlineForm,
  Row,
  Sel,
  StatusBadge,
  Text,
  useAsync,
} from "./components";

const ITEM_TYPES = [
  { value: "HTML_PACKAGE", label: "HTML package" },
  { value: "DOWNLOADABLE_RESOURCE", label: "Downloadable resource" },
  { value: "EXTERNAL_LINK", label: "External link" },
];

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export function ModulePage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const { data: m, error, reload } = useAsync(() => api.getModule(id), [id]);

  const [title, setTitle] = useState("");
  const [required, setRequired] = useState(true);
  const [itemTitle, setItemTitle] = useState("");
  const [itemType, setItemType] = useState("HTML_PACKAGE");
  const [externalUrl, setExternalUrl] = useState("");

  useEffect(() => {
    if (m) {
      setTitle(m.title);
      setRequired(m.required);
    }
  }, [m]);

  if (error)
    return (
      <AdminShell crumbs={[{ label: "Admin", to: "/admin" }]} title="Module">
        {error}
      </AdminShell>
    );
  if (!m)
    return (
      <AdminShell crumbs={[{ label: "Admin", to: "/admin" }]} title="Loading…">
        {null}
      </AdminShell>
    );

  return (
    <AdminShell
      crumbs={[
        { label: "Admin", to: "/admin" },
        { label: m.track.title, to: `/admin/tracks/${m.track.id}` },
        { label: m.title },
      ]}
      title={m.title}
    >
      <Card>
        <div
          style={{ display: "flex", gap: "0.6rem", alignItems: "center", marginBottom: "0.8rem" }}
        >
          <StatusBadge status={m.status} />
          <Btn
            kind="ghost"
            onClick={async () => {
              await api.setPublished("module", m.id, m.status !== "PUBLISHED");
              reload();
            }}
          >
            {m.status === "PUBLISHED" ? "Unpublish" : "Publish"}
          </Btn>
          {m.track.status !== "PUBLISHED" && (
            <span style={{ color: "#b00020", fontSize: "0.8rem" }}>
              Track must be published first
            </span>
          )}
        </div>
        <InlineForm
          submitLabel="Save"
          onSubmit={async () => {
            await api.updateModule(m.id, { title, required });
            reload();
          }}
        >
          <Field label="Title">
            <Text value={title} onChange={(e) => setTitle(e.target.value)} required />
          </Field>
          <label style={{ display: "block", marginBottom: "0.7rem", fontSize: "0.9rem" }}>
            <input
              type="checkbox"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
            />{" "}
            Required to complete the track
          </label>
        </InlineForm>
      </Card>

      <Card>
        <strong>Learning items</strong>
        {m.learningItems.length === 0 && <p style={{ color: "#666" }}>No items yet.</p>}
        {m.learningItems
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((li) => (
            <Row key={li.id}>
              <Link to={`/admin/learning-items/${li.id}`}>
                {li.order + 1}. {truncate(li.title, 100)}
                <span style={{ color: "#888" }}> · {li.type.replace(/_/g, " ").toLowerCase()}</span>
                {li.type === "HTML_PACKAGE" && !li.htmlPackage && (
                  <span style={{ color: "#b00020" }}> · no package uploaded</span>
                )}
              </Link>
              <StatusBadge status={li.status} />
            </Row>
          ))}
        <div style={{ marginTop: "0.8rem" }}>
          <InlineForm
            submitLabel="Add item"
            onSubmit={async () => {
              const { id: liid } = await api.createLearningItem({
                moduleId: m.id,
                type: itemType,
                title: itemTitle,
                order: m.learningItems.length,
                ...(itemType === "EXTERNAL_LINK" ? { externalUrl } : {}),
              });
              setItemTitle("");
              setExternalUrl("");
              nav(`/admin/learning-items/${liid}`);
            }}
          >
            <Field label="Item title / objective (up to 1000 characters, ~4-5 lines)">
              <Area
                value={itemTitle}
                onChange={(e) => setItemTitle(e.target.value)}
                rows={3}
                required
              />
            </Field>
            <Field label="Type">
              <Sel value={itemType} onChange={(e) => setItemType(e.target.value)}>
                {ITEM_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Sel>
            </Field>
            {itemType === "EXTERNAL_LINK" && (
              <Field label="URL">
                <Text
                  type="url"
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  required
                />
              </Field>
            )}
          </InlineForm>
        </div>
      </Card>
    </AdminShell>
  );
}
